/* eslint-disable @typescript-eslint/no-explicit-any */
import { Injectable } from '@angular/core';
import { Observable } from 'rxjs';
import { map, shareReplay } from 'rxjs/operators';
import { NewsService } from 'src/app/shared/services/data-api/news.service';
import { RoutingService } from 'src/app/service/routing.service';
import { NewsfeedOptions } from '../news-newsfeed-interface/news-feed.interfaces';
import { NewsType } from '../news-newsfeed-interface/news-type.constant';
import { StoriesService } from 'src/app/shared/services/data-api/stories.service';

@Injectable({
  providedIn: 'root',
})
export class NewsFeedService {
  private storyArticlesCache = new Map<string, Observable<any[]>>();

  constructor(
    private newsService: NewsService,
    private storiesService: StoriesService,
    public routing: RoutingService,
  ) {}

  private setNewsContentTypeBasedOnParams(): string {
    if (this.routing.isQuery() || this.routing.isSearch()) {
      return 'search_query';
    } else {
      return 'entities';
    }
  }
  chronologicalNewsfeed(options: NewsfeedOptions): Observable<any> {
    const size = options.custom.size;

    return this.newsService.getAllNews({
      body: { type: this.setNewsContentTypeBasedOnParams(), size: size },
    });
  }

  newsFeed(options: NewsfeedOptions): Observable<any> {
    const size = options.custom.size;
    const fromIndex = options.custom.from_index;
    const body = {
      type: this.setNewsContentTypeBasedOnParams(),
      size: size,
      from_index: fromIndex,
    };

    switch (options.newsType) {
      case NewsType.CHRONOLOGICAL:
        return this.newsService.getAllNews({ body });
      case NewsType.FLAGGED:
        return this.newsService.getFlaggedNews({ body });
      case NewsType.BOOKMARKED:
        return this.newsService.getBookMarkedNews({ body });
      case NewsType.DISMISSED:
        return this.newsService.getDismissedNews({ body });
      case NewsType.EVENTS:
        return this.newsService.getEventSnippets(options);
      default:
        console.warn(`Unknown newsType: "${options.newsType}"`);
        return new Observable();
    }
  }

  getStoryArticles(storyId: string): Observable<any[]> {
    const cacheKey = this.buildStoryCacheKey(storyId, 100);

    const request$ = this.storiesService
      .getStoryById(storyId, {
        customParams: {
          min_size: 1,
          size: 100,
        },
        body: { type: this.setNewsContentTypeBasedOnParams() },
      })
      .pipe(
        map((response) => response?.stories?.[0]?.articles ?? []),
        shareReplay(1),
      );

    this.storyArticlesCache.set(cacheKey, request$);
    return request$;
  }

  clearStoryArticlesCache(storyId?: string): void {
    if (!storyId) {
      this.storyArticlesCache.clear();
      return;
    }

    const keyPrefix = `${storyId}:`;
    Array.from(this.storyArticlesCache.keys())
      .filter((key) => key.startsWith(keyPrefix))
      .forEach((key) => this.storyArticlesCache.delete(key));
  }

  private buildStoryCacheKey(storyId: string, size: number): string {
    return `${storyId}:${size}`;
  }
}
